import React from "react";
import i18next from "src/i18n/config";
import { makeStyles } from "tss-react/mui";

import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogProps,
    DialogTitle,
    Theme,
    Typography,
} from "@mui/material";

import { CompactIconButton, SecondaryButton } from "../CustomButton";

const useStyles = makeStyles()((theme: Theme) => ({
    description: { marginBottom: theme.spacing(2) },
    titleBox: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: theme.spacing(1) },
    titleActions: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
    actionBox: { width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" },
    startButtonBox: { display: "flex", gap: theme.spacing(1), flex: 1 },
    endButtonBox: { display: "flex", gap: theme.spacing(1) },
    childrenBox: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
}));

type Props = Omit<DialogProps, "onClose" | "maxWidth" | "fullWidth"> & {
    /**
     * `"sm"` (600px, the default) for confirmations, filters and forms made of fields. `"md"` (900px) for code or
     * JSON, tables with several columns, long identifiers such as hashes, and tabs where one tab holds an editor.
     * The dialog always fills its size, so the content never decides the width.
     */
    maxWidth?: "sm" | "md";
    titleSuffix?: React.ReactNode;
    description?: string | React.JSX.Element;
    /**
     * Additional actions that are not the dialog's purpose (e.g. "Get help", "Expand all"), placed at the left edge.
     */
    startButton?: React.ReactNode;
    /**
     * The dialog's primary action, placed at the right edge after the close button.
     */
    endButton?: React.ReactNode;
    secondaryActionText?: string;
    /**
     * Takes the close button's place, left of the primary action: a node to show there instead of it, or `null` for
     * nothing. Leave the prop out to keep the dialog's own close button. Escape and the backdrop work either way, so
     * `null` is only for a dialog whose content already offers a way back.
     */
    secondaryAction?: React.ReactNode;
    onClose?: (event?: object, reason?: "backdropClick" | "escapeKeyDown") => void;
};

export const Modal = ({
    title,
    titleSuffix = null,
    description,
    startButton,
    endButton,
    secondaryActionText = i18next.t("common:button.close"),
    secondaryAction,
    ...props
}: React.PropsWithChildren<Props>) => {
    const { classes } = useStyles();

    const handleClose = (event?: object, reason?: "backdropClick" | "escapeKeyDown") => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        props.onClose?.(event, reason);
    };

    const closeButton = props.onClose ? <SecondaryButton onClick={handleClose}>{secondaryActionText}</SecondaryButton> : null;

    // Escape is handled by MUI alone, which calls `onClose` with "escapeKeyDown" and honours `disableEscapeKeyDown`.
    // Do not add a second Escape handler here or in a dialog: `onClose` would run twice, and a dialog whose
    // `onClose` toggles its open state reopens straight away.
    return (
        <Dialog fullWidth {...props} onClose={handleClose}>
            <DialogTitle>
                <Box className={classes.titleBox}>
                    <Typography variant="h6">{title}</Typography>
                    <Box className={classes.titleActions}>
                        {titleSuffix}
                        {props.onClose ? (
                            <CompactIconButton
                                onClick={() => handleClose()}
                                tooltipProps={{ title: i18next.t("common:tooltip.closeDialog") }}
                            >
                                <i className="fa-solid fa-xmark" />
                            </CompactIconButton>
                        ) : null}
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {description ? <DialogContentText className={classes.description}>{description}</DialogContentText> : null}
                <Box className={classes.childrenBox}>{props.children}</Box>
            </DialogContent>

            <DialogActions>
                <Box className={classes.actionBox}>
                    <Box className={classes.startButtonBox}>{startButton}</Box>

                    <Box className={classes.endButtonBox}>
                        {secondaryAction === undefined ? closeButton : secondaryAction}
                        {endButton}
                    </Box>
                </Box>
            </DialogActions>
        </Dialog>
    );
};
